// Per-slug quote questionnaire configuration.
// Each entry powers /quote/:slug with tailored copy + extra questions
// that get appended to the booking's description field.

export const SERVICE_DROPDOWN = [
  "TV Mounting",
  "General Mounting",
  "Furniture Assembly",
  "Drywall",
  "Painting",
  "Moving",
  "Junk Removal",
  "Carpentry",
  "Electrical",
  "Plumbing",
];

const f = (id, label, type = "text", opts = {}) => ({ id, label, type, ...opts });

export const QUOTE_CONFIG = {
  // ───────── Services ─────────
  "furniture-assembly": {
    kind: "service",
    serviceType: "Furniture Assembly",
    title: "Furniture Assembly Quote",
    headline: "Furniture assembly services that save you time.",
    intro:
      "New furniture shouldn't take all day to put together. We assemble beds, desks, tables, cabinets, shelving, and more with care and precision.",
    perfectFor: ["New home setups", "Apartment moves", "Office furniture", "Rental properties"],
    questions: [
      f("pieces", "What pieces need assembling?", "textarea", {
        placeholder: "Queen bed, IKEA Kallax shelf, dining table…",
        required: true,
      }),
      f("brand", "Brand(s) (IKEA, Wayfair, Amazon, etc.)"),
      f("count", "Approximate piece count", "number", { min: 1 }),
      f("started", "Any pieces already started?", "select", {
        options: ["No", "Yes — partially assembled"],
      }),
    ],
  },

  "tv-mounting": {
    kind: "service",
    serviceType: "TV Mounting",
    title: "TV Mounting Quote",
    headline: "Professional TV mounting & home installations.",
    intro:
      "Create a cleaner, more functional space with secure TV mounting and wall installations.",
    perfectFor: ["TV mounting", "Shelves", "Mirrors", "Artwork", "Curtain rods", "Wall accessories"],
    questions: [
      f("tvSize", "TV size (inches)", "number", { min: 20, max: 110, required: true }),
      f("mount", "Do you have a mount already?", "select", {
        options: ["Yes, I have one", "No, please bring one", "Need help picking"],
        required: true,
      }),
      f("wall", "Wall type", "select", {
        options: ["Drywall", "Brick", "Concrete", "Not sure"],
        required: true,
      }),
      f("cable", "Cable management?", "select", {
        options: [
          "No, surface cables are fine",
          "Yes, hide cables in wall",
          "Add a new outlet behind TV",
        ],
      }),
    ],
  },

  "moving-help": {
    kind: "service",
    serviceType: "Moving",
    title: "Moving Help Quote",
    headline: "Reliable moving assistance when you need extra hands.",
    intro:
      "Not every move requires a full moving company. We provide help with the heavy lifting and setup.",
    perfectFor: ["Loading & unloading", "Furniture placement", "Rearranging rooms", "Move-in setup"],
    questions: [
      f("helpers", "How many helpers needed?", "number", { min: 1, max: 6, required: true }),
      f("hours", "Estimated hours", "number", { min: 1, max: 12 }),
      f("truck", "Do you have a truck?", "select", {
        options: ["Yes, I have one", "No, just need extra hands"],
      }),
      f("scope", "Loading, unloading, or both?", "select", {
        options: ["Loading only", "Unloading only", "Both"],
      }),
    ],
  },

  "junk-removal": {
    kind: "service",
    serviceType: "Junk Removal",
    title: "Junk Removal Quote",
    headline: "Clear out unwanted items quickly.",
    intro:
      "Moving is the perfect time to remove clutter. We help you clear unwanted items and leave your space clean and ready.",
    perfectFor: ["Furniture", "Boxes", "Household items", "General clutter"],
    questions: [
      f("items", "What needs to be removed?", "textarea", { required: true }),
      f("volume", "Approximate volume", "select", {
        options: ["Single item", "Half truckload", "Full truckload", "Multiple loads"],
        required: true,
      }),
      f("stairs", "Stairs involved?", "select", { options: ["No", "Yes"] }),
    ],
  },

  "painting": {
    kind: "service",
    serviceType: "Painting",
    title: "Painting Services Quote",
    headline: "Fresh paint. Fresh start.",
    intro:
      "Whether you're moving in, moving out, or updating your home, we provide clean and reliable painting services.",
    perfectFor: ["Interior painting", "Touch-ups", "Move-out repairs", "Accent walls"],
    questions: [
      f("rooms", "Rooms / areas to paint", "textarea", { required: true }),
      f("sqft", "Approximate square footage", "number", { min: 50 }),
      f("paint", "Paint provided?", "select", {
        options: ["Yes, I have paint", "No, please provide"],
      }),
      f("colors", "How many colors?", "number", { min: 1, max: 10 }),
    ],
  },

  "general-handyman": {
    kind: "service",
    serviceType: null, // user picks
    title: "General Handyman Quote",
    headline: "Small repairs, installations & home improvement.",
    intro:
      "Tell us what's on your list — drywall patches, fixture swaps, hardware installs, the whole honey-do list. We handle it.",
    perfectFor: ["Drywall patching", "Light fixtures", "Door hardware", "Small repairs"],
    questions: [
      f("primary", "Primary service needed", "select", {
        options: SERVICE_DROPDOWN,
        required: true,
      }),
      f("tasks", "Describe your project list", "textarea", { required: true }),
      f("count", "Approximate number of tasks", "number", { min: 1 }),
    ],
  },

  // ───────── Personas ─────────
  "homeowners": {
    kind: "persona",
    title: "Homeowner Quote",
    headline: "Cross your project list off — without lifting a finger.",
    intro:
      "Tell us what needs doing around the house and we'll bundle it into one efficient visit.",
    questions: [
      f("primary", "Primary service needed", "select", { options: SERVICE_DROPDOWN, required: true }),
      f("tasks", "What's on your list?", "textarea", { required: true }),
    ],
  },

  "renters": {
    kind: "persona",
    title: "Renter Quote",
    headline: "Get set up — or get your deposit back.",
    intro:
      "Move-in setup or move-out cleanup, we'll make sure your place is ready.",
    questions: [
      f("stage", "Moving in or moving out?", "select", {
        options: ["Moving in", "Moving out", "Both"],
        required: true,
      }),
      f("primary", "Primary service needed", "select", { options: SERVICE_DROPDOWN, required: true }),
      f("tasks", "What needs to be done?", "textarea", { required: true }),
    ],
  },

  "landlords": {
    kind: "persona",
    title: "Landlord Quote",
    headline: "Turn over rental properties — fast.",
    intro:
      "We help landlords prep units quickly between tenants. Drywall, paint, mounting, junk hauling — all in one visit.",
    questions: [
      f("units", "How many units?", "number", { min: 1 }),
      f("primary", "Primary service needed", "select", { options: SERVICE_DROPDOWN, required: true }),
      f("tasks", "What needs to be done?", "textarea", { required: true }),
      f("recurring", "Is this recurring work?", "select", {
        options: ["One-time", "Recurring — between tenants", "Recurring — monthly maintenance"],
      }),
    ],
  },

  "realtors": {
    kind: "persona",
    title: "Realtor Quote",
    headline: "Get listings show-ready — and close on time.",
    intro:
      "Pre-listing prep, pre-closing repairs, staging support. Move fast without sacrificing quality.",
    questions: [
      f("stage", "Pre-listing or pre-closing?", "select", {
        options: ["Pre-listing", "Pre-closing", "Inspection repairs"],
        required: true,
      }),
      f("primary", "Primary service needed", "select", { options: SERVICE_DROPDOWN, required: true }),
      f("tasks", "What needs attention?", "textarea", { required: true }),
      f("deadline", "Hard deadline?", "text", { placeholder: "Closing date or open house date" }),
    ],
  },

  "property-managers": {
    kind: "persona",
    title: "Property Manager Quote",
    headline: "One reliable partner for ongoing maintenance.",
    intro:
      "We integrate with your maintenance workflow — predictable response times, transparent quotes, clean job documentation.",
    questions: [
      f("portfolio", "Portfolio size (units)", "number", { min: 1 }),
      f("primary", "Primary service needed", "select", { options: SERVICE_DROPDOWN, required: true }),
      f("tasks", "What's the request?", "textarea", { required: true }),
      f("recurring", "Engagement type", "select", {
        options: ["One-time", "Recurring — on-call", "Recurring — scheduled"],
      }),
    ],
  },
};

export const SERVICE_SLUGS = [
  "furniture-assembly",
  "tv-mounting",
  "moving-help",
  "junk-removal",
  "painting",
  "general-handyman",
];

export const PERSONA_SLUGS = [
  "homeowners",
  "renters",
  "landlords",
  "realtors",
  "property-managers",
];
